import i18n from '@/utils/i18n';
import React, { useRef, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FlatList, Platform, Text, View } from 'react-native';
import {
  HorizontalList,
  Props as HorizontalListProps,
} from './horizontal-list';
import { ListOption } from './list-item';
import { NavigationDots } from './navigation-dots';
import { TYPOGRAPHY } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';

type Props<T extends ListOption> = Omit<
  HorizontalListProps<T>,
  'limit' | 'onSeeAllPress'
> & {
  hasFeaturedTag?: boolean;
  itemCount: number;
  itemWidth: number;
  limit?: number;
  onSeeAllPress?: () => void;
  showNavigationDots?: boolean;
  visibleItemCount: number;
};

export const CatalogCarousel = <T extends ListOption>({
  hasFeaturedTag,
  itemCount,
  itemWidth,
  limit = 10,
  onLayout,
  onScroll,
  showNavigationDots,
  style,
  visibleItemCount,
  ...rest
}: Props<T>) => {
  const { isSmallLayout } = useLayoutContext();
  const [styles] = useStyles();

  const listRef = useRef<FlatList>(null);
  const listContentWidthRef = useRef(0);
  const scrollOffsetRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasLayout, setHasLayout] = useState(false);

  const renderArrows = isSmallLayout
    ? Platform.OS === 'web'
    : itemCount > visibleItemCount;

  return (
    <>
      <HorizontalList<T>
        contentContainerStyle={
          isSmallLayout ? styles.contentContainerSmall : null
        }
        itemWidth={itemWidth}
        keyExtractor={(item) => String(item.value)}
        limit={limit}
        ListHeaderTagComponent={
          hasFeaturedTag ? (
            <View style={styles.featuredTag}>
              <Text style={TYPOGRAPHY.xsmall}>{i18n.t('featured')}</Text>
            </View>
          ) : null
        }
        listRef={listRef}
        onContentSizeChange={(width) => {
          listContentWidthRef.current = width;
        }}
        onLayout={(e) => {
          onLayout?.(e);
          setHasLayout(true);
        }}
        onLeftArrowPress={
          renderArrows
            ? () => {
                listRef.current?.scrollToOffset({
                  animated: true,
                  offset: Math.max(0, scrollOffsetRef.current - itemWidth),
                });
              }
            : undefined
        }
        onMomentumScrollEnd={(e) => {
          const { contentOffset, contentSize, layoutMeasurement } =
            e.nativeEvent;
          const offsetX = contentOffset.x;
          // When the last cell is narrower than itemWidth, max scroll never
          // reaches (itemCount-1)*itemWidth, so Math.round leaves the last
          // dot inactive — force last index near the content end.
          const distanceToEnd =
            contentSize.width - (offsetX + layoutMeasurement.width);
          const isAtEnd =
            contentSize.width > layoutMeasurement.width &&
            distanceToEnd < itemWidth * 0.5;
          setActiveIndex(
            isAtEnd ? itemCount - 1 : Math.round(offsetX / itemWidth),
          );
        }}
        onRightArrowPress={
          renderArrows
            ? () => {
                listRef.current?.scrollToOffset({
                  animated: true,
                  offset: Math.min(
                    listContentWidthRef.current,
                    scrollOffsetRef.current + itemWidth,
                  ),
                });
              }
            : undefined
        }
        onScroll={(e) => {
          scrollOffsetRef.current = e.nativeEvent.contentOffset.x;
          onScroll?.(e);
        }}
        scrollEventThrottle={100}
        style={[style, hasLayout ? styles.listVisible : styles.list]}
        {...rest}
      />
      {isSmallLayout && showNavigationDots ? (
        <View style={styles.navigationDotsContainer}>
          <NavigationDots
            activeIndex={activeIndex}
            count={Math.min(itemCount, limit)}
            onChange={(index) => {
              setActiveIndex(index);
              if (index === Math.min(itemCount, limit) - 1) {
                listRef.current?.scrollToEnd({ animated: true });
              } else {
                listRef.current?.scrollToOffset({
                  animated: true,
                  offset: index * itemWidth,
                });
              }
            }}
          />
        </View>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  contentContainerSmall: {
    paddingHorizontal: 16,
    paddingVertical: 4,
  },
  featuredTag: {
    backgroundColor: theme.colors.white,
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  list: {
    opacity: 0,
  },
  listVisible: {
    opacity: 1,
  },
  navigationDotsContainer: {
    height: 20,
    justifyContent: 'center',
  },
}));
